import React, { useState, useEffect, useCallback } from "react";
import { useIsNarrow } from "../hooks/useMediaQuery";
import { Line, Bar } from "react-chartjs-2";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTriangleExclamation, faDumbbell, faCircleExclamation, faArrowTrendUp, faArrowTrendDown, faMinus, faUsers, faUserSlash, faCircleCheck, faChartColumn, faWeightHanging, faListOl, faCalendarDay, faChartPie } from "@fortawesome/free-solid-svg-icons";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { toast } from "react-toastify";
import api from "../services/api";
import styles from "./TeacherAnalyticsDashboard.module.css";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend
);

// Formatação compartilhada de números nos gráficos (eixo Y, labels nas barras/pontos
// e tooltip) — abrevia K/M e aceita uma unidade opcional. Existia antes em 3 formatos
// diferentes pro mesmo valor (kg cru no eixo, "K" sem unidade na barra, "Ton" no
// tooltip) — unificado num único critério: sempre kg, abreviado, unidade só onde
// o número aparece isolado (bom pra print/compartilhamento).
const formatChartNumber = (value, unit = "") => {
  const rawValue = Math.round(Number(value) || 0);
  const abs = Math.abs(rawValue);
  let display;
  if (abs >= 1000000) {
    display = (rawValue / 1000000).toFixed(1) + "M";
  } else if (abs >= 1000) {
    display = (rawValue / 1000).toFixed(0) + "K";
  } else {
    display = String(rawValue);
  }
  return unit ? `${display} ${unit}` : display;
};

// Plugin padrão para mostrar valores nos gráficos
const dataLabelsPlugin = {
  id: "dataLabels",
  afterDatasetsDraw(chart) {
    // Unidade opcional por gráfico (ex.: "kg" nos gráficos de volume) — configurada
    // via options.plugins.dataLabels.unit ao montar cada <Bar>/<Line>. Gráficos de
    // contagem (sessões, dias, exercícios) não passam unit e ficam sem sufixo.
    const unit = chart.config.options?.plugins?.dataLabels?.unit || "";
    chart.data.datasets.forEach((dataset, datasetIndex) => {
      const meta = chart.getDatasetMeta(datasetIndex);
      meta.data.forEach((bar, index) => {
        const value = dataset.data[index];
        if (value === null || value === undefined) return;

        const { x, y } = bar.getProps(["x", "y"]);
        const displayValue = formatChartNumber(value, unit);

        // Lê o token semântico em tempo real — adapta a dark/light mode
        const labelColor = getComputedStyle(document.documentElement).getPropertyValue("--text-0-50").trim() || "rgba(26,26,26,0.70)";
        chart.ctx.fillStyle = labelColor;
        chart.ctx.font = "bold 11px sans-serif";
        chart.ctx.textAlign = "center";
        chart.ctx.textBaseline = "bottom";

        // Para barras: posiciona acima
        if (chart.config.type === "bar") {
          chart.ctx.fillText(displayValue, x, y - 5);
        }
        // Para linhas: posiciona acima do ponto
        else if (chart.config.type === "line") {
          chart.ctx.fillText(displayValue, x, y - 10);
        }
      });
    });
  }
};

ChartJS.register(dataLabelsPlugin);

// Só para professor de academia (user.organization_id + org_role): "org" =
// carteira compartilhada da academia, mesma regra do painel do professor
// (TeacherDashboard) e de /teachers/analytics?scope=.
const SCOPES = [
  { key: "mine", label: "Meus alunos" },
  { key: "org", label: "Todos da academia" },
];

export default function TeacherAnalyticsDashboard({ user }) {
  const hasOrg = !!(user?.organization_id && user?.org_role);
  const [scope, setScope] = useState("mine");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(30);
  const [customInput, setCustomInput] = useState("30");
  const [showCustomDate, setShowCustomDate] = useState(false);
  const [error, setError] = useState(null);
  // Ver hooks/useMediaQuery.js — antes cada componente reimplementava isso.
  const isMobile = useIsNarrow();

  const MAX_DAYS = 180;

  const loadAnalytics = useCallback(async (daysParam) => {
    try {
      setError(null);
      const res = await api.get("/teachers/analytics", {
        params: { days: daysParam, scope: hasOrg ? scope : "mine" },
      });
      setData(res.data);
    } catch (e) {
      const errorMsg = e?.response?.data?.detail || e.message || "Erro ao carregar analytics";
      console.error("Error loading analytics:", e?.response?.status, errorMsg);
      setError(errorMsg);
      setLoading(false);
    } finally {
      setLoading(false);
    }
  }, [hasOrg, scope]);

  useEffect(() => {
    loadAnalytics(days);
  }, [days, loadAnalytics]);

  if (loading) return <div>Carregando...</div>;

  if (error && !data) {
    return (
      <div className={styles.container}>
        <div className={styles.errorMessage}>
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
          <button onClick={() => { setLoading(true); loadAnalytics(days); }} style={{ marginLeft: 12 }}>
            Tentar novamente
          </button>
        </div>
      </div>
    );
  }

  if (!data) return null;

  const tickColor = getComputedStyle(document.documentElement).getPropertyValue("--text-0-50").trim() || "rgba(120,120,120,0.7)";

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: true,
    layout: {
      padding: {
        top: 30,
      },
    },
    plugins: {
      legend: {
        display: false,
      },
      // No mobile os valores já aparecem direto nas barras/pontos via
      // dataLabelsPlugin — o tooltip de toque só duplica a informação e
      // some do jeito errado ao rolar a tela, poluindo a leitura. Ver docs/07-issues.md.
      tooltip: {
        enabled: !isMobile,
      },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: tickColor },
      },
      y: {
        grid: { color: "rgba(200, 200, 200, 0.1)" },
        ticks: { color: tickColor },
      },
    },
  };

  // Variante usada pelos 3 gráficos de volume ("Volume por período", "Volume por
  // grupo muscular", "Carga média por sessão"): eixo Y abreviado (mesmo K/M do label
  // da barra, em vez do número cru "46.000") + unidade "kg" habilitada no data label.
  const volumeChartOptions = {
    ...chartOptions,
    plugins: { ...chartOptions.plugins, dataLabels: { unit: "kg" } },
    scales: {
      ...chartOptions.scales,
      y: {
        ...chartOptions.scales.y,
        ticks: { ...chartOptions.scales.y.ticks, callback: (val) => formatChartNumber(val) },
      },
    },
  };

  const filterButtons = [
    { label: "Últimos 7 dias", value: 7 },
    { label: "Últimos 30 dias", value: 30 },
    { label: "Customizado", value: "custom" },
  ];

  const formatVolume = (volume) => formatChartNumber(volume, "kg");

  const getChartColor = (alpha = 1) => {
    // Converter hex para rgba
    const hex = data.brand_color || "#1d9e75";
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return { hex, rgb: `rgb(${r}, ${g}, ${b})`, rgba: (a) => `rgba(${r}, ${g}, ${b}, ${a})` };
  };

  // ── helpers de delta ──────────────────────────────────────────────────────
  const prev = data.prev_kpis || {};
  function delta(current, previous) {
    if (!previous || previous === 0) return null;
    return Math.round(((current - previous) / previous) * 100);
  }
  function DeltaBadge({ pct, invertColor = false }) {
    if (pct == null) return null;
    const up = pct > 0;
    const neutral = pct === 0;
    // invertColor: para abandono, subir é ruim
    const good = invertColor ? !up : up;
    const color = neutral ? "var(--text-0-40)" : good ? "#34d399" : "#f87171";
    const icon = neutral ? faMinus : good ? faArrowTrendUp : faArrowTrendDown;
    return (
      <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: "var(--fs-xs)", fontWeight: 700, color, marginLeft: 6 }}>
        <FontAwesomeIcon icon={icon} style={{ fontSize: "var(--fs-xs)" }} />
        {Math.abs(pct)}%
      </span>
    );
  }

  const atRisk = data.students_at_risk || [];
  const dist   = data.students_session_distribution || [];

  return (
    <div className={styles.container}>
      {error && (
        <div className={styles.errorMessage}>
          <FontAwesomeIcon icon={faTriangleExclamation} /> {error}
        </div>
      )}

      {/* ── Escopo (academia) ── */}
      {hasOrg && (
        <div className={styles.filterBar} role="group" aria-label="Quais alunos analisar">
          {!isMobile && <p className={styles.filterLabel}>Alunos:</p>}
          {SCOPES.map((s) => (
            <button
              key={s.key}
              type="button"
              onClick={() => setScope(s.key)}
              aria-pressed={scope === s.key}
              className={`${styles.filterBtn} ${scope === s.key ? styles.active : ""}`}
            >
              {s.label}
            </button>
          ))}
        </div>
      )}

      {/* ── Filtros ── */}
      <div className={styles.filterBar}>
        {!isMobile && <p className={styles.filterLabel}>Período:</p>}
        {filterButtons.map((btn) => (
          <button
            key={btn.label}
            onClick={() => {
              if (btn.value === "custom") {
                setShowCustomDate(!showCustomDate);
              } else {
                setDays(btn.value);
                setCustomInput(btn.value.toString());
                setShowCustomDate(false);
              }
            }}
            title={btn.label}
            className={`${styles.filterBtn} ${days === btn.value ? styles.active : ""}`}
          >
            {isMobile ? (btn.label === "Últimos 7 dias" ? "7d" : btn.label === "Últimos 30 dias" ? "30d" : btn.label) : btn.label}
          </button>
        ))}
        {showCustomDate && (
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            <input
              type="number" min="1"
              value={customInput}
              onChange={(e) => {
                const inputVal = e.target.value;
                setCustomInput(inputVal);
                if (inputVal === "" || inputVal === "0") return;
                const numVal = parseInt(inputVal);
                if (isNaN(numVal)) return;
                if (numVal > MAX_DAYS) {
                  toast.warning(`Máximo ${MAX_DAYS} dias!`);
                  setDays(MAX_DAYS); setCustomInput(MAX_DAYS.toString());
                } else if (numVal < 1) {
                  setDays(1); setCustomInput("1");
                } else {
                  setDays(numVal);
                }
              }}
              placeholder="Dias"
              title={`Máximo ${MAX_DAYS} dias`}
              className={styles.filterInput}
            />
            <span className={styles.filterHint}>máx {MAX_DAYS} dias</span>
          </div>
        )}
      </div>

      {/* ── Alunos em risco ── */}
      {atRisk.length > 0 && (
        <div className={styles.atRiskPanel}>
          <div className={styles.atRiskHeader}>
            <FontAwesomeIcon icon={faCircleExclamation} style={{ color: "#f59e0b" }} />
            <span>Atenção necessária</span>
            <span className={styles.atRiskCount}>{atRisk.length} aluno{atRisk.length !== 1 ? "s" : ""}</span>
          </div>
          <div className={styles.atRiskList}>
            {atRisk.map((s, i) => (
              <div key={i} className={styles.atRiskRow}>
                <span className={styles.atRiskName}>
                  <FontAwesomeIcon icon={faUserSlash} style={{ fontSize: "var(--fs-xs)", opacity: 0.55, marginRight: 6 }} />
                  {s.name}
                </span>
                <span className={styles.atRiskMeta}>
                  {s.days_since_last != null
                    ? `${s.days_since_last}d sem treinar`
                    : "nunca treinou"}
                  {s.sessions_in_period === 0 && s.days_since_last != null
                    ? " · 0 no período"
                    : s.sessions_in_period > 0
                    ? ` · ${s.sessions_in_period} no período`
                    : ""}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── KPIs ── */}
      <div className={styles.kpiGrid}>
        <div className={styles.kpiCard} data-tooltip="Alunos que finalizaram pelo menos 1 sessão de treino no período selecionado">
          <p className={styles.kpiLabel}><FontAwesomeIcon icon={faUsers} style={{ marginRight: 5 }} />Alunos ativos</p>
          <p className={styles.kpiValue}>
            {data.distinct_active_students ?? "-"}
            <span style={{ fontSize: 13, fontWeight: 400, color: "var(--text-0-40)", marginLeft: 5 }}>
              / {data.total_students ?? "-"}
            </span>
          </p>
        </div>
        <div className={styles.kpiCard} data-tooltip="Total de sessões de treino concluídas no período. Seta indica variação vs período anterior.">
          <p className={styles.kpiLabel}>Total de sessões</p>
          <p className={styles.kpiValue}>
            {data.kpis.total_sessions}
            <DeltaBadge pct={delta(data.kpis.total_sessions, prev.total_sessions)} />
          </p>
        </div>
        <div className={styles.kpiCard} data-tooltip="Soma de peso × repetições de todos os exercícios no período. Seta indica variação vs período anterior.">
          <p className={styles.kpiLabel}>Volume agregado</p>
          <p className={styles.kpiValue}>
            {(data.kpis.total_volume_kg / 1000).toFixed(1)}K kg
            <DeltaBadge pct={delta(data.kpis.total_volume_kg, prev.total_volume_kg)} />
          </p>
        </div>
        <div className={styles.kpiCard} data-tooltip="% de exercícios que foram ignorados ou abandonados durante o treino. Acima de 20% é sinal de alerta.">
          <p className={styles.kpiLabel}>Taxa abandono</p>
          <p className={`${styles.kpiValue} ${data.kpis.abandonment_rate > 20 ? styles.kpiValueWarning : ""}`}>
            {data.kpis.abandonment_rate}%
            <DeltaBadge pct={delta(data.kpis.abandonment_rate, prev.abandonment_rate)} invertColor />
          </p>
        </div>
        <div className={styles.kpiCard} data-tooltip="Tempo médio (em minutos) das sessões de treino concluídas no período.">
          <p className={styles.kpiLabel}>Duração média</p>
          <p className={styles.kpiValue}>
            {data.kpis.avg_duration_minutes} min
            <DeltaBadge pct={delta(data.kpis.avg_duration_minutes, prev.avg_duration_minutes)} />
          </p>
        </div>
      </div>

      {/* ── Charts principais (ordem definida pelo usuario, 1 grid unico) ── */}
      <div className={styles.chartsGrid}>
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}><FontAwesomeIcon icon={faCircleCheck} /> Treinos finalizados</p>
          <Line data={{ labels: data.timeline_data.map(d => d.week), datasets: [{ label: "Sessões", data: data.timeline_data.map(d => d.sessions), borderColor: data.brand_color || "#1d9e75", backgroundColor: getChartColor().rgba(0.1), tension: 0.4 }] }} options={chartOptions} height={isMobile ? 150 : 150} />
        </div>

        <div className={styles.chartCard}>
          <p className={styles.chartTitle}><FontAwesomeIcon icon={faChartColumn} /> Volume por período (Últimos {days} dias)</p>
          <Bar
            data={{
              labels: data.volume_weekly.map(d => d.week),
              datasets: [{ label: "Volume", data: data.volume_weekly.map(d => d.volume), backgroundColor: data.brand_color || "#1d9e75" }]
            }}
            options={{
              ...volumeChartOptions,
              plugins: { ...volumeChartOptions.plugins, tooltip: { enabled: !isMobile, callbacks: { label: ctx => `${formatVolume(ctx.parsed.y)}` } } }
            }}
            height={isMobile ? 150 : 150}
          />
        </div>

        <div className={styles.chartCard}>
          <p className={styles.chartTitle}><FontAwesomeIcon icon={faDumbbell} /> Volume por grupo muscular</p>
          {data.volume_by_muscle_group?.length > 0 ? (
            <Bar
              data={{
                labels: data.volume_by_muscle_group.map(d => {
                  const n = d.muscle_group;
                  return n.charAt(0).toUpperCase() + n.slice(1);
                }),
                datasets: [{
                  label: "Volume (kg)",
                  data: data.volume_by_muscle_group.map(d => d.volume),
                  backgroundColor: ["#4f8ef7","#38bdf8","#facc15","#fb923c","#a78bfa","#34d399","#f472b6"].slice(0, data.volume_by_muscle_group.length),
                  borderRadius: 4,
                }]
              }}
              options={{
                ...volumeChartOptions,
                plugins: { ...volumeChartOptions.plugins, tooltip: { enabled: !isMobile, callbacks: { label: ctx => `${formatVolume(ctx.parsed.y)}` } } }
              }}
              height={isMobile ? 150 : 150}
            />
          ) : (
            <p className={styles.emptyMessage}>Sem dados no período</p>
          )}
        </div>

        {/* Carga média por sessão ao longo do tempo */}
        {(() => {
          const tl = data.timeline_data || [];
          const vw = data.volume_weekly || [];
          if (!tl.length || !vw.length) return null;
          const labels = tl.map(d => d.week);
          const avgLoad = tl.map((d, i) => {
            const sessions = d.sessions || 0;
            const volume = vw[i]?.volume || 0;
            return sessions > 0 ? Math.round(volume / sessions) : 0;
          });
          const hasData = avgLoad.some(v => v > 0);
          if (!hasData) return null;
          return (
            <div className={styles.chartCard}>
              <p className={styles.chartTitle}><FontAwesomeIcon icon={faWeightHanging} /> Carga média por sessão</p>
              <Line
                data={{
                  labels,
                  datasets: [{
                    label: "kg/sessão",
                    data: avgLoad,
                    borderColor: "#a78bfa",
                    backgroundColor: "rgba(167,139,250,0.10)",
                    tension: 0.4,
                    fill: true,
                    pointRadius: 4,
                    pointBackgroundColor: "#a78bfa",
                  }]
                }}
                options={{
                  ...volumeChartOptions,
                  plugins: {
                    ...volumeChartOptions.plugins,
                    tooltip: {
                      enabled: !isMobile,
                      callbacks: {
                        label: ctx => `${formatVolume(ctx.parsed.y)} / sessão`
                      }
                    }
                  }
                }}
                height={isMobile ? 150 : 150}
              />
              <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 6 }}>
                Volume total ÷ sessões no período - mede intensidade média
              </div>
            </div>
          );
        })()}

        <div className={styles.chartCard}>
          <p className={styles.chartTitle}><FontAwesomeIcon icon={faListOl} /> Exercícios mais executados</p>
          <Bar data={{ labels: data.top_exercises.map(d => d.name), datasets: [{ label: "Execuções", data: data.top_exercises.map(d => d.count), backgroundColor: data.brand_color || "#1d9e75" }] }} options={chartOptions} height={isMobile ? 150 : 150} />
        </div>

        <div className={styles.chartCard}>
          <p className={styles.chartTitle}><FontAwesomeIcon icon={faCalendarDay} /> Dias mais ativos</p>
          <Bar data={{ labels: data.days_active.map(d => d.day), datasets: [{ label: "Treinos", data: data.days_active.map(d => d.count), backgroundColor: data.brand_color || "#1d9e75" }] }} options={chartOptions} height={isMobile ? 150 : 150} />
        </div>

        {/* Distribuição de aderência */}
        <div className={styles.chartCard}>
          <p className={styles.chartTitle}><FontAwesomeIcon icon={faChartPie} /> Distribuição de aderência</p>
          {dist.length > 0 ? (
            <div className={styles.distList}>
              {dist.map((b, i) => {
                const total = dist.reduce((s, x) => s + x.count, 0) || 1;
                const pct = Math.round((b.count / total) * 100);
                return (
                  <div key={i} className={styles.distRow}>
                    <span className={styles.distLabel}>{b.label}</span>
                    <div className={styles.distBarWrap}>
                      <div className={styles.distBar} style={{ width: `${pct}%`, background: b.color }} />
                    </div>
                    <span className={styles.distCount} style={{ color: b.color }}>
                      {b.count} <span style={{ color: "var(--text-0-40)", fontWeight: 400 }}>({pct}%)</span>
                    </span>
                  </div>
                );
              })}
              <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 8 }}>
                Total: {data.total_students ?? "-"} alunos · período: {days} dias
              </div>
            </div>
          ) : (
            <p className={styles.emptyMessage}>Sem dados</p>
          )}
        </div>

        {/* Alunos com exercícios problemáticos */}
        {(data.top_students_with_issues?.length > 0) && (
          <div className={styles.chartCard}>
            <p className={styles.chartTitle}><FontAwesomeIcon icon={faTriangleExclamation} /> Exercícios problemáticos por aluno</p>
            <div className={styles.issuesList}>
              {data.top_students_with_issues.map((s, i) => (
                <div key={i} className={styles.issueRow}>
                  <span className={styles.studentName}>{s.name}</span>
                  <div className={styles.issueCount}>
                    {s.abandoned > 0 && (
                      <span className={styles.abandonedCount} title="Exercícios abandonados (auto-preenchidos pelo sistema)">
                        {s.abandoned} aband.
                      </span>
                    )}
                    {s.incomplete > 0 && (
                      <span className={styles.incompleteCount} title="Exercícios com séries zeradas">
                        {s.incomplete} incomp.
                      </span>
                    )}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: "var(--fs-xs)", color: "var(--text-0-40)", marginTop: 10 }}>
              Exercícios auto-preenchidos pelo sistema ou com reps = 0
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
